/**
 * Products (techladder-core). One catalog for every account: TechLadder admins manage it
 * through `/api/v2/admin/products`, accounts view it through `/api/v2/products`.
 * Prices are rupees.
 *
 * A product with a range is a credits plan: `price` is rupees per minute, and an
 * account buys it for any whole-rupee amount in the range, getting amount ÷ price
 * credits (1 credit = 1 minute of calling), rounded up.
 */

export interface Product {
  id: string;
  name: string;
  price: number;
  description: string | null;
  /** Credits plans: the least and most one purchase can be (rupees, excl. GST). */
  min_amount: number | null;
  /** null: no upper limit. */
  max_amount: number | null;
}

export interface ProductPage {
  items: Product[];
  next_cursor: string | null;
  limit: number;
}

export interface ProductInput {
  name: string;
  price: number;
  /** null clears it; a blank description is stored as none. */
  description: string | null;
  min_amount: number | null;
  max_amount: number | null;
}

/** Mirrors the API's limits so the form rejects what the server would. */
export const PRODUCT_NAME_MAX = 200;
export const PRODUCT_PRICE_MAX = 1_000_000_000;
export const PRODUCT_DESCRIPTION_MAX = 2000;

/**
 * A description written one point per line reads as a feature list. Leading
 * bullets the admin may have typed ("-", "•", "*", "✓") are dropped.
 */
export const descriptionLines = (description: string | null): string[] =>
  (description ?? "")
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*(?:[-•*✓✔]\s*)?/, "").trim())
    .filter(Boolean);

export const rupees = (value: number) =>
  `₹${value.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/** A product bought for an amount within a range, rather than by quantity. */
export const isPlan = (product: Pick<Product, "min_amount" | "max_amount">) =>
  product.min_amount !== null || product.max_amount !== null;

/** "₹1,000 – ₹50,000", or "₹1,00,001 and above" when there is no upper limit. */
export const amountRange = ({ min_amount, max_amount }: Pick<Product, "min_amount" | "max_amount">) =>
  max_amount === null ? `${rupees(min_amount ?? 1)} and above` : `${rupees(min_amount ?? 1)} – ${rupees(max_amount)}`;

/** Why an amount cannot buy this plan, or null when it can. */
export function planAmountError(product: Pick<Product, "min_amount" | "max_amount">, raw: string): string | null {
  if (!raw.trim()) return "Enter an amount.";
  if (!/^\d+$/.test(raw.trim())) return "Enter whole rupees, e.g. 40000.";
  const amount = Number(raw);
  const min = product.min_amount ?? 1;
  if (amount < min || (product.max_amount !== null && amount > product.max_amount)) {
    return `Choose ${amountRange(product)}.`;
  }
  return null;
}

/**
 * Credits an amount buys at a per-minute rate, rounded up (₹50,000 at ₹6/min is
 * 8,334). A preview only: the server works out what the order actually gets.
 */
export const creditsFor = (amount: number, ratePerMinute: number) =>
  ratePerMinute > 0 ? Math.ceil(Number((amount / ratePerMinute).toFixed(6))) : 0;

export const credits = (value: number) => value.toLocaleString("en-IN");
